// ============================================================================
// PÁGINA: REVALIDA PRO
// Arquivo: src/pages/servicos/RevalidaPro.tsx
// UPDATED: fluxo em 2 fases — formulário → uploads (via UploadDocumentos)
// ============================================================================

import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Upload, CheckCircle2, AlertTriangle } from "lucide-react";
import UploadDocumentos from "../components/Filiacao/UploadDocumentos";

import { API_BASE } from '../utils/apiConfig';

type Fase = "formulario" | "uploads" | "concluido";

type FormState = {
  nomeCompleto: string;
  dataNascimento: string;
  cpf: string;
  cnpj: string;
  email: string;
  telefone: string;
  cep: string;
  endereco: string;
  numero: string;
  cidade: string;
  estado: string;
  complemento: string;
  historicoProfissional: string;
  redesSociais: string;
};

const UFS = [
  "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB",
  "PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO",
];

// Documentos específicos do Revalida Pro
const DOCUMENTOS_REVALIDA = [
  {
    id: "doc_certif",
    titulo: "Certificado de Formação",
    descricao: "Certificado na área de atuação (se houver)",
    arquivo: null,
    obrigatorio: false,
    tiposAceitos: [".pdf", ".jpg", ".jpeg", ".png"],
    tamanhoMaxMB: 5,
    documentType: "certificado" as const,
  },
  {
    id: "doc_carta",
    titulo: "Declaração de Experiência Profissional",
    descricao: "Obrigatória caso não possua certificado formal",
    arquivo: null,
    obrigatorio: false, // validação condicional feita no UploadDocumentos via prop
    tiposAceitos: [".pdf", ".jpg", ".jpeg", ".png"],
    tamanhoMaxMB: 5,
    documentType: "homologacao" as const,
  },
  {
    id: "doc_id",
    titulo: "Documento de Identificação (RG, CPF ou CNH)",
    descricao: "Frente e verso em um único arquivo, se possível",
    arquivo: null,
    obrigatorio: true,
    tiposAceitos: [".pdf", ".jpg", ".jpeg", ".png"],
    tamanhoMaxMB: 5,
    documentType: "rg" as const,
  },
  {
    id: "doc_end",
    titulo: "Comprovante de Endereço",
    descricao: "Conta de água, luz, internet, etc.",
    arquivo: null,
    obrigatorio: true,
    tiposAceitos: [".pdf", ".jpg", ".jpeg", ".png"],
    tamanhoMaxMB: 5,
    documentType: "comprovante" as const,
  },
];

const RevalidaPro: React.FC = () => {
  const requisitos = [
    "Certificado de formação na área de atuação, quando houver",
    "Na ausência de certificação formal, Declaração de Experiência e Competência Profissional",
    "Portfólio de trabalhos ou registros profissionais (incluindo redes sociais), quando disponível",
    "Documento oficial de identificação (RG, CPF ou CNH)",
    "Comprovante de endereço residencial",
    "CNPJ, se houver (não obrigatório para o processo)",
  ];

  const beneficios = [
    "Diploma FEPCSSP-BR, acompanhado de Identificação Profissional em PVC, com registro profissional e selo de rastreabilidade, em conformidade com padrões internacionais de qualidade",
    "Acesso a canal exclusivo no WhatsApp, com comunicados institucionais, novidades e benefícios com condições especiais para os profissionais Federados",
    "Suporte nacional e internacional para processos de validação profissional em mais de 130 países, por meio da Federação",
  ];

  // ── Estado principal ────────────────────────────────────────────────────────
  const [fase, setFase] = useState<Fase>("formulario");
  const [affiliateId, setAffiliateId] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [tentouEnviar, setTentouEnviar] = useState(false);
  const [erroBackend, setErroBackend] = useState<string | null>(null);

  const [form, setForm] = useState<FormState>({
    nomeCompleto: "",
    dataNascimento: "",
    cpf: "",
    cnpj: "",
    email: "",
    telefone: "",
    cep: "",
    endereco: "",
    numero: "",
    cidade: "",
    estado: "",
    complemento: "",
    historicoProfissional: "",
    redesSociais: "",
  });

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleChange =
    (key: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

  // ── Validação ───────────────────────────────────────────────────────────────
  const errors = useMemo(() => {
    const list: string[] = [];
    if (!form.nomeCompleto.trim())         list.push("Informe o Nome Completo.");
    if (!form.dataNascimento)              list.push("Informe a Data de Nascimento.");
    if (form.cpf.replace(/\D/g, "").length !== 11) list.push("CPF inválido (11 dígitos).");
    if (!form.email.includes("@"))         list.push("Informe um e-mail válido.");
    if (!form.cep.trim())                  list.push("Informe o CEP.");
    if (!form.endereco.trim())             list.push("Informe o Endereço.");
    if (!form.numero.trim())               list.push("Informe o Número.");
    if (!form.cidade.trim())               list.push("Informe a Cidade.");
    if (!form.estado.trim())               list.push("Selecione o Estado.");
    if (!form.historicoProfissional.trim()) list.push("Descreva seu histórico profissional.");
    if (!form.redesSociais.trim())         list.push("Informe o link das redes sociais.");
    return list;
  }, [form]);

  // ── Submit do formulário → chama /revalida/register ─────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTentouEnviar(true);
    setErroBackend(null);

    if (errors.length > 0) return;

    setEnviando(true);

    try {
      const payload = {
        nomeCompleto:          form.nomeCompleto,
        dataNascimento:        form.dataNascimento || undefined,
        cpf:                   form.cpf,
        cnpj:                  form.cnpj || undefined,
        email:                 form.email,
        telefone:              form.telefone || undefined,
        cep:                   form.cep,
        endereco:              form.endereco,
        numero:                form.numero,
        complemento:           form.complemento || undefined,
        cidade:                form.cidade,
        estado:                form.estado,
        historicoProfissional: form.historicoProfissional,
        redesSociais:          form.redesSociais,
      };

      const response = await fetch(`${API_BASE}/revalida/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
        throw new Error(err?.message || `Erro ao registrar (${response.status})`);
      }

      const result = await response.json();
      // Backend deve retornar { id: string (UUID), systemId: string }
      setAffiliateId(result.id);
      setFase("uploads");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err: any) {
      setErroBackend(err?.message || "Falha ao enviar. Tente novamente.");
    } finally {
      setEnviando(false);
    }
  };

  // ── Callbacks do UploadDocumentos ───────────────────────────────────────────
  const handleUploadSucesso = (_results: { id: string; url: string }[]) => {
    setFase("concluido");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Limpar ──────────────────────────────────────────────────────────────────
  const limparFormulario = () => {
    setForm({
      nomeCompleto: "", dataNascimento: "", cpf: "", cnpj: "",
      email: "", telefone: "", cep: "", endereco: "", numero: "",
      cidade: "", estado: "", complemento: "", historicoProfissional: "", redesSociais: "",
    });
    setTentouEnviar(false);
    setErroBackend(null);
  };

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50/30 text-gray-900">

      {/* ── HERO ── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-amber-950 to-gray-950" />
        <div className="container mx-auto px-6 relative z-10 py-20 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-8">
            <img src="/icons/icon4.png" alt="Revalida Pro" className="w-6 h-6" />
            <span className="text-sm font-bold text-amber-100 uppercase tracking-wider">Regularização Profissional</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white">REVALIDA PRO</h1>
          <p className="mt-6 text-lg text-white/85 max-w-3xl mx-auto">
            Sistema criado para validar e regularizar a situação profissional de quem atua nas áreas de Beleza e Estética,
            mas ainda não possui diploma oficial reconhecido ou registro profissional.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="#formulario" className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-amber-600 to-orange-600 text-white font-bold rounded-xl shadow-lg hover:scale-105 transition">
              <span>Preencher Formulário</span>
              <ArrowRight className="w-5 h-5" />
            </a>
            <Link to="/filiacao" className="inline-flex items-center gap-3 px-8 py-4 bg-white/10 border border-white/20 backdrop-blur-md text-white font-bold rounded-xl hover:bg-white/15 transition">
              <span>Ver Filiação</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── O QUE É ── */}
      <section className="py-20">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="text-center mb-14">
            <img src="/icons/icone16.png" alt="O que é" className="w-12 h-12 mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-black">O que é o REVALIDA PRO?</h2>
          </div>
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
            <p className="text-lg text-gray-700 leading-relaxed">
              O <strong className="text-amber-600">REVALIDA PRO</strong> é um sistema criado para validar e regularizar a
              situação profissional de indivíduos que atuam nas áreas de Beleza, Estética e suas subcategorias, mas que
              ainda não possuem diploma oficial reconhecido pelas autoridades competentes ou registro profissional através
              da Federação das Escolas.
            </p>
          </div>
        </div>
      </section>

      {/* ── COMO FUNCIONA ── */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-14">
            <img src="/icons/icon7.png" alt="Como funciona" className="w-12 h-12 mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-black">Como funciona o REVALIDA PRO?</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { img: "/icons/icone2.png", title: "Comprovação", desc: "Apresente certificados, portfólio ou carta de experiência profissional." },
              { img: "/icons/icon3.png",  title: "Análise",     desc: "A Federação analisa seu perfil e define o melhor caminho de regularização." },
              { img: "/icons/icon4.png",  title: "Validação",   desc: "Após aprovação, ocorre a emissão da documentação profissional." },
            ].map((item, idx) => (
              <div key={idx} className="bg-gradient-to-br from-white to-amber-50 rounded-3xl border border-amber-100 p-8 shadow-lg hover:shadow-xl transition">
                <img src={item.img} alt={item.title} className="w-14 h-14 mb-5" />
                <h3 className="font-black text-xl mb-2">{item.title}</h3>
                <p className="text-gray-700">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── REQUISITOS ── */}
      <section className="py-20">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="text-center mb-14">
            <img src="/icons/icon4.png" alt="Requisitos" className="w-12 h-12 mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-black">Requisitos para Revalidação</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {requisitos.map((req, idx) => (
              <div key={idx} className="flex items-start gap-4 bg-white rounded-2xl p-5 border border-amber-100 shadow-sm">
                <img src="/icons/icon4.png" alt="Item" className="w-6 h-6 mt-1" />
                <p className="text-gray-800 font-semibold">{req}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          FORMULÁRIO — 2 FASES
      ══════════════════════════════════════════════════════════════════════ */}
      <section id="formulario" className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-6xl">

          {/* ── Cabeçalho + indicador de fase ── */}
          <div className="text-center mb-14">
            <img src="/icons/icon10.png" alt="Formulário" className="w-12 h-12 mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-black">Formulário Revalida Pro</h2>
            <p className="mt-3 text-gray-600 max-w-3xl mx-auto">
              Preencha os dados e anexe seus documentos. A equipe fará a validação e retornará com o valor de
              investimento conforme análise do perfil.
            </p>

            {/* Indicador de etapas */}
            <div className="mt-8 inline-flex items-center gap-2">
              {(["formulario", "uploads", "concluido"] as Fase[]).map((f, idx) => {
                const labels = ["1. Dados", "2. Documentos", "3. Concluído"];
                const active = fase === f;
                const done =
                  (f === "formulario" && (fase === "uploads" || fase === "concluido")) ||
                  (f === "uploads" && fase === "concluido");
                return (
                  <React.Fragment key={f}>
                    {idx > 0 && <div className="w-8 h-0.5 bg-gray-200" />}
                    <div
                      className={`px-4 py-1.5 rounded-full text-sm font-bold transition ${
                        active
                          ? "bg-amber-600 text-white"
                          : done
                          ? "bg-green-100 text-green-700 border border-green-200"
                          : "bg-gray-100 text-gray-400"
                      }`}
                    >
                      {done ? "✓ " : ""}{labels[idx]}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* ── FASE: concluído ── */}
          {fase === "concluido" && (
            <div className="max-w-xl mx-auto text-center">
              <div className="bg-gradient-to-br from-emerald-50 to-white rounded-3xl border border-emerald-200 p-10 shadow-xl">
                <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                <h3 className="text-2xl font-black text-gray-900 mb-3">Solicitação enviada!</h3>
                <p className="text-gray-700 mb-6">
                  Seus dados e documentos foram recebidos. Nossa equipe fará a análise e entrará em contato pelo
                  e-mail informado em até <strong>2 dias úteis</strong>.
                </p>
                <p className="text-sm text-gray-500 mb-8">
                  <strong>Prazo de emissão:</strong> até 10 dias úteis após validação da documentação.
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 text-white font-bold shadow hover:scale-105 transition"
                >
                  Voltar ao início
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}

          {/* ── FASE: uploads ── */}
          {fase === "uploads" && affiliateId && (
            <div className="bg-gradient-to-br from-white to-amber-50 rounded-3xl border border-amber-100 shadow-xl p-6 md:p-10">
              <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4">
                <p className="text-sm text-amber-900">
                  <strong>Ótimo!</strong> Seus dados foram salvos. Agora anexe os documentos abaixo para finalizar sua
                  solicitação.
                </p>
              </div>
              <UploadDocumentos
                affiliateId={affiliateId}
                documentosIniciais={DOCUMENTOS_REVALIDA}
                onDocumentosSalvos={() => {}}
                onUploadSucesso={handleUploadSucesso}
              />
            </div>
          )}

          {/* ── FASE: formulário ── */}
          {fase === "formulario" && (
            <div className="bg-gradient-to-br from-white to-amber-50 rounded-3xl border border-amber-100 shadow-xl p-6 md:p-10">

              {/* Alertas de validação */}
              {tentouEnviar && errors.length > 0 && (
                <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-5">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-700 mt-0.5 shrink-0" />
                    <div>
                      <div className="font-black text-red-900">Revise os campos abaixo</div>
                      <ul className="mt-2 text-sm text-red-800 list-disc pl-5 space-y-1">
                        {errors.map((e, i) => <li key={i}>{e}</li>)}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {erroBackend && (
                <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-5">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-700 mt-0.5 shrink-0" />
                    <div>
                      <div className="font-black text-red-900">Erro ao enviar</div>
                      <p className="mt-1 text-sm text-red-800">{erroBackend}</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-10">

                {/* ── Dados Pessoais ── */}
                <fieldset>
                  <SectionHeader icon="/icons/icon4.png" title="Dados Pessoais" />
                  <div className="grid md:grid-cols-2 gap-5">
                    <Field label="Nome Completo *">
                      <input value={form.nomeCompleto} onChange={handleChange("nomeCompleto")}
                        className={inputClass} placeholder="Digite seu nome completo" />
                    </Field>
                    <Field label="Data de Nascimento *">
                      <input type="date" value={form.dataNascimento} onChange={handleChange("dataNascimento")}
                        className={inputClass} />
                    </Field>
                    <Field label="CPF *">
                      <input value={form.cpf} onChange={handleChange("cpf")}
                        className={inputClass} placeholder="Somente números" inputMode="numeric" />
                    </Field>
                    <Field label="CNPJ (opcional)">
                      <input value={form.cnpj} onChange={handleChange("cnpj")}
                        className={inputClass} placeholder="Somente números" inputMode="numeric" />
                    </Field>
                    <Field label="E-mail *">
                      <input type="email" value={form.email} onChange={handleChange("email")}
                        className={inputClass} placeholder="seuemail@dominio.com" />
                    </Field>
                    <Field label="Telefone / WhatsApp">
                      <input type="tel" value={form.telefone} onChange={handleChange("telefone")}
                        className={inputClass} placeholder="(00) 00000-0000" />
                    </Field>
                  </div>
                </fieldset>

                {/* ── Endereço ── */}
                <fieldset>
                  <SectionHeader icon="/icons/icon6.png" title="Endereço" />
                  <div className="grid md:grid-cols-2 gap-5">
                    <Field label="CEP *">
                      <input value={form.cep} onChange={handleChange("cep")}
                        className={inputClass} placeholder="00000-000" inputMode="numeric" />
                    </Field>
                    <Field label="Estado (UF) *">
                      <select value={form.estado} onChange={handleChange("estado")} className={inputClass}>
                        <option value="">Selecione</option>
                        {UFS.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
                      </select>
                    </Field>
                    <div className="md:col-span-2 grid md:grid-cols-3 gap-5">
                      <div className="md:col-span-2">
                        <Field label="Endereço *">
                          <input value={form.endereco} onChange={handleChange("endereco")}
                            className={inputClass} placeholder="Rua / Avenida" />
                        </Field>
                      </div>
                      <Field label="Nº *">
                        <input value={form.numero} onChange={handleChange("numero")}
                          className={inputClass} placeholder="Número" />
                      </Field>
                    </div>
                    <Field label="Cidade *">
                      <input value={form.cidade} onChange={handleChange("cidade")}
                        className={inputClass} placeholder="Sua cidade" />
                    </Field>
                    <Field label="Complemento">
                      <input value={form.complemento} onChange={handleChange("complemento")}
                        className={inputClass} placeholder="Apto / Bloco / Referência" />
                    </Field>
                  </div>
                </fieldset>

                {/* ── Histórico Profissional ── */}
                <fieldset>
                  <SectionHeader icon="/icons/icon7.png" title="Histórico Profissional" />
                  <div className="grid gap-5">
                    <Field label="Descrição do histórico profissional *"
                      hint="Inclua datas e evolução desde o primeiro curso">
                      <textarea value={form.historicoProfissional} onChange={handleChange("historicoProfissional")}
                        rows={6} className={inputClass}
                        placeholder="Ex.: 2018: primeiro curso... 2020: comecei a atender... 2023: especialização..." />
                    </Field>
                    <Field label="Link das redes sociais *">
                      <input value={form.redesSociais} onChange={handleChange("redesSociais")}
                        className={inputClass} placeholder="Instagram, TikTok, portfólio, etc." />
                    </Field>
                  </div>
                </fieldset>

                {/* Aviso sobre uploads */}
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-2xl bg-white border border-amber-100 shadow flex items-center justify-center shrink-0">
                      <Upload className="w-5 h-5 text-amber-700" />
                    </div>
                    <p className="text-sm text-amber-900 pt-2">
                      Após enviar este formulário, você avançará para a <strong>etapa de anexos</strong>, onde fará o
                      upload dos documentos (certificado ou declaração de experiência, documento oficial e comprovante de endereço).
                    </p>
                  </div>
                </div>

                {/* Ações */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2">
                  <button type="button" onClick={limparFormulario}
                    className="px-6 py-3 rounded-xl border border-gray-200 bg-white font-bold text-gray-900 hover:bg-gray-50 transition">
                    Limpar formulário
                  </button>
                  <button type="submit" disabled={enviando}
                    className="px-8 py-3.5 rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 text-white font-black shadow-lg hover:shadow-2xl hover:scale-[1.02] transition flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                    {enviando ? "Enviando..." : "Avançar para Documentos"}
                    {!enviando && <ArrowRight className="w-5 h-5" />}
                  </button>
                </div>

                <p className="text-xs text-gray-500">
                  Ao enviar, você concorda em compartilhar os dados informados para análise do processo de revalidação.
                </p>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* ── BENEFÍCIOS ── */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-14">
            <img src="/icons/icon10.png" alt="Benefícios" className="w-12 h-12 mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-black">Benefícios do Revalida Pro</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {beneficios.map((beneficio, idx) => (
              <div key={idx} className="bg-white rounded-3xl border border-amber-100 p-8 shadow-lg hover:shadow-xl transition">
                <img src="/icons/icon6.png" alt="Benefício" className="w-14 h-14 mb-4" />
                <p className="text-gray-800 font-bold leading-relaxed">{beneficio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA FINAL ── */}
      <section className="py-20 bg-gradient-to-br from-gray-950 via-amber-950 to-gray-950 text-white text-center">
        <div className="container mx-auto px-6 max-w-4xl">
          <img src="/icons/icon6.png" alt="Internacional" className="w-16 h-16 mx-auto mb-6" />
          <p className="text-2xl md:text-3xl font-black mb-8">
            Regularize sua atuação profissional com respaldo nacional e internacional.
          </p>
          <Link to="/filiacao"
            className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-amber-600 to-orange-600 font-bold rounded-xl shadow-lg hover:scale-105 transition">
            <span>Quero iniciar</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

// ── Sub-componentes locais ─────────────────────────────────────────────────────

const inputClass =
  "w-full px-4 py-3 rounded-2xl border border-gray-200 bg-white focus:ring-2 focus:ring-amber-500 focus:border-transparent transition";

const SectionHeader: React.FC<{ icon: string; title: string }> = ({ icon, title }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className="w-10 h-10 rounded-2xl bg-white border border-amber-100 shadow flex items-center justify-center overflow-hidden shrink-0">
      <img src={icon} alt={title} className="w-6 h-6 object-contain" />
    </div>
    <h3 className="text-xl font-black text-gray-900">{title}</h3>
  </div>
);

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({
  label, hint, children,
}) => (
  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-2">
      {label}
      {hint && <span className="font-normal text-gray-500 ml-1">({hint})</span>}
    </label>
    {children}
  </div>
);

export default RevalidaPro;